/** Chat workspace header: title, status chips and the history / audio / new-chat actions. */
import React from 'react';
import { TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '@/design-system';
import { VoiceUploadButton } from '../../../components/voice';
import { useTranslation } from '../../../hooks/useTranslation';
import { webChatStyles } from '../chatStyles';

export interface ChatHeaderProps {
  title: string;
  isPhone: boolean;
  isCompact: boolean;
  showTranscriptReady: boolean;
  showAnalysisDone: boolean;
  creditLabel: string;
  onOpenHistory: () => void;
  onNewChat: () => void;
  onAudioFileSelected: (file: { uri: string; type: string; name: string; size?: number }) => void;
  audioLoading: boolean;
  audioDisabled: boolean;
}

export function ChatHeader({
  title,
  isPhone,
  isCompact,
  showTranscriptReady,
  showAnalysisDone,
  creditLabel,
  onOpenHistory,
  onNewChat,
  onAudioFileSelected,
  audioLoading,
  audioDisabled,
}: ChatHeaderProps) {
  const { t } = useTranslation();
  const chipStyle = [
    webChatStyles.statusChip,
    isCompact && webChatStyles.statusChipCompact,
    isPhone && webChatStyles.statusChipPhone,
  ];
  const chipTextStyle = [webChatStyles.statusChipText, isPhone && webChatStyles.statusChipTextPhone];

  return (
    <View
      style={[webChatStyles.reviewHeader, isCompact && webChatStyles.reviewHeaderCompact, isPhone && webChatStyles.reviewHeaderTiny]}
      nativeID="chat_workspace_header"
      testID="chat_workspace_header"
    >
      <View style={webChatStyles.reviewHeaderText}>
        <Text style={webChatStyles.reviewTitle} numberOfLines={isPhone ? 2 : 1}>{title}</Text>
        <View style={[webChatStyles.statusRow, isCompact && webChatStyles.statusRowCompact]}>
          {showTranscriptReady && (
            <View style={chipStyle} testID="chat_transcript_ready_chip">
              <MaterialIcons name="description" size={14} color={reviewDesk.accent} />
              <Text style={chipTextStyle}>{t('chat.webTranscriptReady')}</Text>
            </View>
          )}
          {showAnalysisDone && (
            <View style={chipStyle} testID="chat_analysis_done_chip">
              <MaterialIcons name="verified" size={14} color={reviewDesk.accent} />
              <Text style={chipTextStyle}>{t('chat.webAnalysisDone')}</Text>
            </View>
          )}
          <View
            nativeID="credit_badge"
            testID="credit_badge"
            style={chipStyle}
            accessibilityLabel={t('chat.webCreditStatus')}
          >
            <MaterialIcons name="toll" size={14} color={reviewDesk.accent} />
            <Text style={chipTextStyle}>{creditLabel}</Text>
          </View>
        </View>
      </View>
      <View style={[webChatStyles.headerActions, isPhone && webChatStyles.headerActionsCompact]}>
        <TouchableOpacity
          onPress={onOpenHistory}
          style={webChatStyles.headerIconButton}
          accessibilityRole="button"
          accessibilityLabel={t('chat.chatSessions')}
          testID="chat_history_button"
        >
          <MaterialIcons name="history" size={20} color={reviewDesk.inkSoft} />
        </TouchableOpacity>
        <VoiceUploadButton
          onFileSelected={onAudioFileSelected}
          loading={audioLoading}
          disabled={audioDisabled}
          size={38}
          color={reviewDesk.inkSoft}
          backgroundColor={reviewDesk.card}
          borderColor={reviewDesk.line}
          borderWidth={1}
        />
        <TouchableOpacity
          onPress={onNewChat}
          style={webChatStyles.headerIconButton}
          accessibilityRole="button"
          accessibilityLabel={t('chat.newChat')}
          testID="chat_new_button"
        >
          <MaterialIcons name="add" size={20} color={reviewDesk.inkSoft} />
        </TouchableOpacity>
      </View>
    </View>
  );
}
